import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'CAD'];
const CURRENCY_SYMBOLS = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', AUD: 'A$', CAD: 'C$' };
const STORAGE_KEY = 'zelina-currency';
const RATE_CACHE_KEY = 'zelina-fx-rates';
const RATE_CACHE_TTL = 3600000; // 1 hour

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'USD';
  });
  const [rates, setRates] = useState({ USD: 1, EUR: 0.92, GBP: 0.79, AUD: 1.55, CAD: 1.37 }); // fallback defaults

  const fetchRates = useCallback(async () => {
    // Check cache first
    const cached = localStorage.getItem(RATE_CACHE_KEY);
    if (cached) {
      try {
        const { rates: cachedRates, ts } = JSON.parse(cached);
        if (Date.now() - ts < RATE_CACHE_TTL) {
          setRates(cachedRates);
          return;
        }
      } catch {}
    }

    try {
      const API = '';
      const res = await fetch(`${API}/api/exchange-rates`);
      if (res.ok) {
        const data = await res.json();
        setRates(data.rates);
        localStorage.setItem(RATE_CACHE_KEY, JSON.stringify({ rates: data.rates, ts: Date.now() }));
      }
    } catch {
      // Use fallback rates silently
    }
  }, []);

  useEffect(() => {
    // Defer rate fetch to not block initial paint
    const timer = setTimeout(fetchRates, 100);
    return () => clearTimeout(timer);
  }, [fetchRates]);

  const setCurrency = (c) => {
    if (SUPPORTED_CURRENCIES.includes(c)) {
      setCurrencyState(c);
      localStorage.setItem(STORAGE_KEY, c);
    }
  };

  // Convert a USD amount to the selected currency
  const convertPrice = useCallback((usdAmount) => {
    if (currency === 'USD') return usdAmount;
    const rate = rates[currency] || 1;
    return Math.round(usdAmount * rate * 100) / 100;
  }, [currency, rates]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convertPrice, rates, SUPPORTED_CURRENCIES, CURRENCY_SYMBOLS }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
